/**
 * BÁO CÁO CÔNG VIỆC (thêm 22/09/2026): mỗi người mỗi ngày MỘT báo cáo — việc đã làm, số video / thumbnail
 * làm được, giờ làm, dự án chính, vướng mắc, kế hoạch ngày mai. Tổng hợp tháng cộng theo người và
 * đối chiếu số sản phẩm với KPI tuần. Bản ghi `report` có chủ (người báo cáo). Hàm thuần, có test.
 */
import type { RecordDto } from '@halo/shared';
import { toCsv } from './csv';
import { formatFullDate, type CalendarDate } from './dates';
import { compactData, readNumber, readString, type RecordData } from './records/parsing';

/**
 * MỘT DÒNG SẢN PHẨM của báo cáo: một dự án và số video / thumbnail làm cho dự án ấy trong ngày
 * (thêm 23/09/2026 — phòng sản xuất AI và phòng thumbnail chạy nhiều dự án một ngày, một ô «dự án
 * chính» không ghi nổi). `projectId` null = việc lẻ, không gắn dự án nào.
 */
export interface ReportLine {
  projectId: number | null;
  videos: number;
  thumbs: number;
  /** Số BÀI HÁT làm cho dự án ấy — sản phẩm của nhóm Producer (thêm 23/09/2026). */
  songs: number;
}

export interface WorkReport {
  recordId: number;
  owner: string | null;
  /** «DD/MM/YYYY». */
  dateKey: string;
  done: string;
  /** Từng dự án một dòng. Rỗng = ngày đó không khai sản phẩm nào. */
  lines: ReportLine[];
  /** TỔNG video của mọi dòng — suy ra từ `lines`, đừng gán tay (xem `withReportLines`). */
  videos: number;
  /** TỔNG thumbnail của mọi dòng — suy ra từ `lines`. */
  thumbs: number;
  /** TỔNG bài hát của mọi dòng — suy ra từ `lines`. */
  songs: number;
  /** Giờ làm trong ngày (có thể lẻ: 7,5). */
  hours: number;
  /** Dự án CHÍNH (dòng nhiều sản phẩm nhất) — suy ra từ `lines`; null = không gắn dự án nào. */
  projectId: number | null;
  issue: string;
  plan: string;
  /** Lúc lưu lần cuối (ISO). */
  at: string;
}

const count = (data: RecordData, key: string) =>
  Math.max(0, Math.round(readNumber(data, key) ?? 0));

const projectOf = (value: unknown): number | null => {
  const id = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(id) && id > 0 ? Math.round(id) : null;
};
const countOf = (value: unknown): number => {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? Math.max(0, Math.round(amount)) : 0;
};

/**
 * Gộp dòng trùng dự án và bỏ dòng rỗng (không dự án mà cũng không số) — hai dòng cùng một dự án
 * đọc ra hai con số rời, cộng lại mới đúng là làm bao nhiêu cho dự án ấy.
 */
export function normalizeReportLines(lines: readonly ReportLine[]): ReportLine[] {
  const merged = new Map<number, ReportLine>();
  for (const line of lines) {
    const projectId = line.projectId ?? 0;
    const videos = Math.max(0, Math.round(line.videos));
    const thumbs = Math.max(0, Math.round(line.thumbs));
    const songs = Math.max(0, Math.round(line.songs));
    if (!projectId && !videos && !thumbs && !songs) continue;
    const current = merged.get(projectId);
    if (current) {
      current.videos += videos;
      current.thumbs += thumbs;
      current.songs += songs;
    } else {
      merged.set(projectId, { projectId: projectId || null, videos, thumbs, songs });
    }
  }
  return [...merged.values()];
}

const sumOf = (lines: readonly ReportLine[], key: 'videos' | 'thumbs' | 'songs') =>
  lines.reduce((total, line) => total + line[key], 0);

const lineProducts = (line: ReportLine) => line.videos + line.thumbs + line.songs;

/** Dự án CHÍNH: dòng nhiều sản phẩm nhất; hoà thì dòng đứng trước. */
function mainProject(lines: readonly ReportLine[]): number | null {
  let best: ReportLine | null = null;
  for (const line of lines) {
    if (!line.projectId) continue;
    if (!best || lineProducts(line) > lineProducts(best)) best = line;
  }
  return best?.projectId ?? null;
}

/**
 * Đặt lại danh sách dòng và tính lại mọi số suy ra từ nó. Chỗ duy nhất được sửa `lines`.
 *
 * KHÔNG gộp / bỏ dòng ở đây: đang gõ dở thì dòng mới còn trống, dọn ngay là nó biến mất dưới tay
 * người ta (bấm «Thêm dự án» không ra dòng nào). Việc dọn để lúc ghi xuống — `serializeWorkReport`.
 */
export function withReportLines<T extends WorkReport>(report: T, lines: readonly ReportLine[]): T {
  const next = lines.map((line) => ({ ...line }));
  return {
    ...report,
    lines: next,
    videos: sumOf(next, 'videos'),
    thumbs: sumOf(next, 'thumbs'),
    songs: sumOf(next, 'songs'),
    projectId: mainProject(next),
  };
}

/**
 * Đọc `pjs` (danh sách dòng). Bản ghi CŨ chưa có `pjs` thì dựng MỘT dòng từ `pj` / `v` / `t` — báo
 * cáo cũ hiện ra y như trước, không cần ai dọn dữ liệu.
 */
function readLines(data: RecordData): ReportLine[] {
  const raw = data.pjs;
  if (Array.isArray(raw)) {
    return normalizeReportLines(
      raw.flatMap((item) =>
        Array.isArray(item)
          ? [
              {
                projectId: projectOf(item[0]),
                videos: countOf(item[1]),
                thumbs: countOf(item[2]),
                // Dòng CŨ chỉ có ba ô (chưa có bài hát) — thiếu thì là 0.
                songs: countOf(item[3]),
              },
            ]
          : [],
      ),
    );
  }
  return normalizeReportLines([
    {
      projectId: projectOf(data.pj),
      videos: count(data, 'v'),
      thumbs: count(data, 't'),
      songs: count(data, 's'),
    },
  ]);
}

export function parseWorkReport(record: RecordDto): WorkReport {
  const data = record.data;
  const lines = readLines(data);
  return {
    recordId: record.id,
    owner: record.owner,
    dateKey: readString(data, 'd'),
    done: readString(data, 'done'),
    lines,
    videos: sumOf(lines, 'videos'),
    thumbs: sumOf(lines, 'thumbs'),
    songs: sumOf(lines, 'songs'),
    hours: Math.max(0, readNumber(data, 'h') ?? 0),
    projectId: mainProject(lines),
    issue: readString(data, 'issue'),
    plan: readString(data, 'plan'),
    at: readString(data, 'at'),
  };
}

/**
 * Chữ rỗng, số 0, không dự án thì KHÔNG ghi khoá — bản ghi chỉ giữ những gì có thật.
 *
 * `v` / `t` / `pj` VẪN được ghi dù đã có `pjs`: đó là tổng và dự án chính, để mọi chỗ đọc báo cáo
 * (tổng hợp tuần / tháng, xuất CSV, đối chiếu KPI) dùng thẳng mà không phải tự cộng lại. `pjs` chỉ
 * ghi khi có từ hai dòng trở lên — một dòng thì `pj` / `v` / `t` đã nói đủ.
 */
export function serializeWorkReport(
  report: Pick<WorkReport, 'dateKey' | 'done' | 'lines' | 'hours' | 'issue' | 'plan' | 'at'>,
): RecordData {
  const lines = normalizeReportLines(report.lines);
  return compactData({
    d: report.dateKey,
    done: report.done.trim() || undefined,
    pjs:
      lines.length > 1
        ? lines.map((line) => [line.projectId ?? 0, line.videos, line.thumbs, line.songs])
        : undefined,
    v: sumOf(lines, 'videos') || undefined,
    t: sumOf(lines, 'thumbs') || undefined,
    s: sumOf(lines, 'songs') || undefined,
    h: report.hours || undefined,
    pj: mainProject(lines) ?? undefined,
    issue: report.issue.trim() || undefined,
    plan: report.plan.trim() || undefined,
    at: report.at,
  });
}

export function newWorkReport(owner: string, date: CalendarDate): WorkReport {
  return {
    recordId: 0,
    owner,
    dateKey: formatFullDate(date),
    done: '',
    lines: [],
    videos: 0,
    thumbs: 0,
    songs: 0,
    hours: 0,
    projectId: null,
    issue: '',
    plan: '',
    at: '',
  };
}

/** Lý do chưa lưu được, hoặc null: phải ghi việc đã làm HOẶC có số sản phẩm / giờ. */
export function reportProblem(
  report: Pick<WorkReport, 'done' | 'videos' | 'thumbs' | 'songs' | 'hours'>,
) {
  if (report.done.trim() || report.videos || report.thumbs || report.songs || report.hours) {
    return null;
  }
  return 'Ghi việc đã làm, hoặc số video / thumbnail / bài hát / giờ làm — báo cáo trống thì không lưu';
}

/** SẢN PHẨM của một ngày: video + thumbnail + bài hát — con số đem so với KPI tuần. */
export const productsOf = (report: Pick<WorkReport, 'videos' | 'thumbs' | 'songs'>) =>
  report.videos + report.thumbs + report.songs;

export function reportKey(owner: string | null, dateKey: string): string {
  return `${owner ?? ''}|${dateKey}`;
}

/** Một người một ngày một báo cáo: trùng thì bản ghi mới hơn (id lớn hơn) thắng. */
export function latestReports(reports: readonly WorkReport[]): Map<string, WorkReport> {
  const map = new Map<string, WorkReport>();
  for (const report of reports) {
    const key = reportKey(report.owner, report.dateKey);
    const current = map.get(key);
    if (!current || current.recordId < report.recordId) map.set(key, report);
  }
  return map;
}

/** Báo cáo của một ngày (đã khử trùng). */
export function reportsOn(index: ReadonlyMap<string, WorkReport>, dateKey: string): WorkReport[] {
  return [...index.values()].filter((report) => report.dateKey === dateKey);
}

/**
 * Ai phải báo cáo: `expected` (nhân sự đang làm, trừ giám đốc) cộng thêm bất kỳ ai đã có báo cáo trong các
 * ngày `dateKeys` mà không nằm trong danh sách — để không giấu báo cáo nào.
 */
export function reporters(
  expected: readonly string[],
  index: ReadonlyMap<string, WorkReport>,
  dateKeys: readonly string[],
): string[] {
  const wanted = new Set(dateKeys);
  const known = new Set(expected);
  const extras = new Set<string>();
  for (const report of index.values()) {
    if (report.owner && wanted.has(report.dateKey) && !known.has(report.owner)) {
      extras.add(report.owner);
    }
  }
  return [...expected, ...[...extras].sort()];
}

/** Những người trong `people` chưa có báo cáo ngày `dateKey`. */
export function missingReporters(
  people: readonly string[],
  index: ReadonlyMap<string, WorkReport>,
  dateKey: string,
): string[] {
  return people.filter((username) => !index.has(reportKey(username, dateKey)));
}

// ---------------------------------------------------------------- lưới NGƯỜI × NGÀY

export interface DayCell {
  dateKey: string;
  /** Báo cáo của người ấy ngày ấy; vắng mặt = chưa báo cáo. */
  report: WorkReport | undefined;
  products: number;
  hours: number;
}

export interface PersonDays {
  username: string;
  cells: DayCell[];
  /** Số ngày CÓ báo cáo trong dải. */
  days: number;
  products: number;
  hours: number;
}

/**
 * LƯỚI NGƯỜI × NGÀY (25/09/2026) — cả phòng trong một màn hình.
 *
 * Màn báo cáo cá nhân chỉ xem được MỘT ngày một lúc, còn Tổng hợp tháng thì dồn cả tháng vào một
 * con số mỗi người: muốn biết «tuần này ai đang chững lại» thì phải bấm từng ngày hoặc mở từng
 * người. Lưới này giữ cả hai chiều — mỗi người một dòng, mỗi ngày một cột — nên nhìn một cái là
 * thấy ai trống ngày nào.
 *
 * Người giữ đúng thứ tự `people` gọi vào (đã lọc theo thanh «Xem»), không tự sắp lại: dòng nhảy
 * chỗ mỗi lần đổi ngày thì mắt phải dò lại từ đầu.
 */
export function buildPersonDays(
  people: readonly string[],
  index: ReadonlyMap<string, WorkReport>,
  dateKeys: readonly string[],
): PersonDays[] {
  return people.map((username) => {
    const cells = dateKeys.map((dateKey) => {
      const report = index.get(reportKey(username, dateKey));
      return {
        dateKey,
        report,
        products: report ? productsOf(report) : 0,
        hours: report?.hours ?? 0,
      };
    });
    return {
      username,
      cells,
      days: cells.filter((cell) => cell.report).length,
      products: cells.reduce((sum, cell) => sum + cell.products, 0),
      hours: cells.reduce((sum, cell) => sum + cell.hours, 0),
    };
  });
}

/**
 * Xếp lưới: ai CÓ báo cáo lên trước (nhiều ngày nhất, rồi nhiều sản phẩm nhất), người im lặng cả
 * dải dồn xuống cuối theo tên.
 *
 * Giữ nguyên thứ tự danh bạ thì với 52 người mà chỉ 3 người báo cáo, ba dòng có số nằm rải rác
 * giữa gần năm chục dòng trống — đúng cái cảnh «đi tìm từng người» mà màn này sinh ra để bỏ.
 */
export function sortByActivity(
  rows: readonly PersonDays[],
  displayName: (username: string) => string,
): PersonDays[] {
  return [...rows].sort(
    (a, b) =>
      b.days - a.days ||
      b.products - a.products ||
      displayName(a.username).localeCompare(displayName(b.username), 'vi'),
  );
}

export interface DayTotals {
  dateKey: string;
  /** Bao nhiêu người trong lưới đã báo cáo ngày này. */
  reported: number;
  products: number;
  hours: number;
}

/** Cộng theo CỘT: mỗi ngày bao nhiêu người báo cáo, bao nhiêu sản phẩm, bao nhiêu giờ. */
export function dayTotals(rows: readonly PersonDays[], dateKeys: readonly string[]): DayTotals[] {
  return dateKeys.map((dateKey, column) => {
    const cells = rows.map((row) => row.cells[column]).filter((cell) => cell !== undefined);
    return {
      dateKey,
      reported: cells.filter((cell) => cell.report).length,
      products: cells.reduce((sum, cell) => sum + cell.products, 0),
      hours: cells.reduce((sum, cell) => sum + cell.hours, 0),
    };
  });
}

/** Lưới người × ngày thành CSV: cột đầu là tên, mỗi ngày một cột số sản phẩm. */
export function personDaysCsv(
  rows: readonly PersonDays[],
  dateKeys: readonly string[],
  displayName: (username: string) => string,
  dayLabel: (dateKey: string) => string,
): string {
  return toCsv([
    ['Nhân viên', ...dateKeys.map(dayLabel), 'Ngày có báo cáo', 'Sản phẩm', 'Giờ làm'],
    ...rows.map((row) => [
      displayName(row.username),
      // Chưa báo cáo để TRỐNG chứ không ghi 0: 0 là «có báo cáo mà không ra sản phẩm», khác hẳn.
      ...row.cells.map((cell) => (cell.report ? String(cell.products) : '')),
      String(row.days),
      String(row.products),
      String(row.hours),
    ]),
  ]);
}
